import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/auth.store';

interface AdminSidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

const IconDashboard = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const IconSubscriptions = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const IconEstablishments = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const IconAuditLogs = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const IconBilling = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconSupport = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const IconCreateUsers = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
  </svg>
);

const IconLogout = ({ isActive }: { isActive: boolean }) => (
  <svg className={`w-5 h-5 ${isActive ? 'text-amber-600' : 'text-neutral-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

const iconMap: Record<string, React.FC<{ isActive: boolean }>> = {
  dashboard: IconDashboard,
  subscriptions: IconSubscriptions,
  establishments: IconEstablishments,
  'create-users': IconCreateUsers,
  'audit-logs': IconAuditLogs,
  billing: IconBilling,
  support: IconSupport,
  logout: IconLogout,
};

export default function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { t } = useTranslation();

  const adminNavItems = [
    { href: '/admin', label: t('adminSidebar.navDashboard'), icon: 'dashboard', exact: true },
    { href: '/admin/subscriptions', label: t('adminSidebar.navSubscriptions'), icon: 'subscriptions' },
    { href: '/admin/establishments', label: t('adminSidebar.navEstablishments'), icon: 'establishments' },
    { href: '/admin/create-users', label: t('adminSidebar.navCreateUsers'), icon: 'create-users' },
    { href: '/admin/audit-logs', label: t('adminSidebar.navAuditLogs'), icon: 'audit-logs' },
    { href: '/admin/billing', label: t('adminSidebar.navBilling'), icon: 'billing' },
    { href: '/admin/support', label: t('adminSidebar.navSupport'), icon: 'support' },
  ];

  const handleNavClick = () => {
    onClose?.();
  };

  return (
    <>
      <div className="hidden lg:flex lg:w-72 bg-gradient-to-b from-neutral-900 via-neutral-800 to-neutral-900 text-white flex-col fixed left-0 top-0 h-screen border-r border-neutral-700 shadow-lg">
        <AdminSidebarContent
          location={location}
          user={user}
          navItems={adminNavItems}
          onNavClick={handleNavClick}
          t={t}
        />
      </div>

      <div
        className={`fixed left-0 top-0 h-screen w-72 max-w-full bg-gradient-to-b from-neutral-900 via-neutral-800 to-neutral-900 text-white flex flex-col z-30 transition-transform duration-300 border-r border-neutral-700 shadow-lg lg:hidden ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <AdminSidebarContent
          location={location}
          user={user}
          navItems={adminNavItems}
          onNavClick={handleNavClick}
          t={t}
        />
      </div>
    </>
  );
}

interface AdminSidebarContentProps {
  location: ReturnType<typeof useLocation>;
  user: any;
  navItems: Array<{ href: string; label: string; icon: string; exact?: boolean }>;
  onNavClick?: () => void;
  t: (key: string) => string;
}

function AdminSidebarContent({
  location,
  user,
  navItems,
  onNavClick,
  t,
}: AdminSidebarContentProps) {
  const logout = useAuthStore((state) => state.logout);

  return (
    <>
      <div className="px-4 py-4 border-b border-neutral-700 bg-neutral-900">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
            <span className="text-2xl">👑</span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-lg leading-tight">
              <span className="text-amber-400">HACCP</span>
              <span className="text-white"> ADMIN</span>
            </span>
            <span className="text-[10px] text-neutral-400 font-medium tracking-wide uppercase leading-tight">
              {t('adminSidebar.adminPanel')}
            </span>
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {navItems.map((item) => {
          const isActive = item.exact
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href);
          const IconComponent = iconMap[item.icon];

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavClick}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300 font-medium group relative overflow-hidden ${
                isActive
                  ? 'bg-gradient-to-r from-amber-600/30 to-amber-500/20 text-amber-400 shadow-md border-l-4 border-amber-500'
                  : 'text-neutral-300 hover:bg-gradient-to-r hover:from-neutral-700/50 hover:to-neutral-600/30 hover:text-white hover:shadow-md'
              }`}
            >
              {!isActive && (
                <div className="absolute inset-0 bg-gradient-to-r from-neutral-700/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
              )}
              {IconComponent && <IconComponent isActive={isActive} />}
              <span className="text-sm flex-1">{item.label}</span>
              {isActive && (
                <div className="w-2 h-2 rounded-full bg-amber-500 shadow-md animate-pulse"></div>
              )}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-neutral-700 p-4 space-y-3 bg-gradient-to-t from-neutral-900 via-neutral-800 to-transparent">
        <div className="flex items-center gap-3 px-2 group cursor-pointer transition-transform duration-300 hover:scale-105">
          {user?.profilePictureUrl ? (
            <img
              src={user.profilePictureUrl}
              alt={user?.displayName || t('adminSidebar.administrator')}
              className="w-10 h-10 rounded-full object-cover shadow-lg ring-2 ring-amber-500/50 group-hover:ring-amber-500 transition-all duration-300"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-amber-500/50 group-hover:ring-amber-500 transition-all duration-300">
              {user?.displayName?.[0]?.toUpperCase() || 'A'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white truncate">
              {user?.displayName || t('adminSidebar.administrator')}
            </p>
            <p className="text-xs text-amber-400 font-medium">{t('adminSidebar.superAdmin')}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-300 hover:bg-gradient-to-r hover:from-red-600/20 hover:to-red-500/10 hover:text-red-400 rounded-lg transition-all duration-300 font-medium border border-neutral-600 hover:border-red-500/40 group shadow-sm hover:shadow-md"
        >
          <IconLogout isActive={false} />
          <span>{t('adminSidebar.logout')}</span>
        </button>
      </div>
    </>
  );
}
